// Redux/env-connected wrappers around the presentational PlayerCardView.
//
// The design system ships the presentational components (PlayerCardView) — they
// take versionAssets / assetsBaseUrl / tier as props and have no Redux or env
// coupling. These wrappers keep the original app-facing API (read versionAssets +
// tier from the store, asset base URL from env, build the nav URL) so existing
// call-sites and examples don't change.
import {
  contentLabelsSelector,
  eventTokensSelector,
  getVersionAssetsSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import {
  BasePath,
  generateNavigationUrl,
  playerRoleLabelSelector,
  tierSelector,
  versionUrlSlug,
} from "@/redux/players/playersSlice";
import type { EvolutionFooterDetails } from "@repo/shared/evolutions";
import {
  BasePlayer,
  Chemistry,
  Player,
  isEvolution as isEvolutionFunction,
  isEvolutionPlayer,
} from "@repo/shared/types";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import * as View from "./PlayerCardView";

export const PlayerCardWithMetaRatingSkeleton =
  View.PlayerCardWithMetaRatingSkeleton;

export const EmptyPlayerCard = View.EmptyPlayerCard;

const PlayerCard = ({
  player,
  position,
  scale = 1,
  isEvolution,
  hidePrice,
  chemStyleId,
  chemistry,
  chemistryScale,
  firstOwner,
}: {
  player: BasePlayer;
  position: string;
  scale?: number;
  isEvolution?: boolean;
  hidePrice?: boolean;
  /** Applied chemstyle glyph under the position; hidden when omitted. */
  chemStyleId?: number;
  /** The player's chemistry (0–3), on the card's bottom-left corner. */
  chemistry?: Chemistry;
  /** The chemistry chip's own scale (see the view's PlayerCard). */
  chemistryScale?: number;
  /** EA's first-owner glyph under the position. */
  firstOwner?: boolean;
}) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const contentLabels = useSelector(contentLabelsSelector);
  const eventTokens = useSelector(eventTokensSelector);
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  return (
    <View.PlayerCard
      player={player}
      position={position}
      scale={scale}
      isEvolution={isEvolution}
      hidePrice={hidePrice}
      chemStyleId={chemStyleId}
      chemistry={chemistry}
      chemistryScale={chemistryScale}
      firstOwner={firstOwner}
      versionAssets={versionAssets}
      assetsBaseUrl={assetsBaseUrl}
      contentLabels={contentLabels}
      eventTokens={eventTokens}
    />
  );
};

export const PlayerCardWithMetaRating = ({
  player,
  asLink,
  onClick,
  scale = 1,
  fullWidth = false,
  hideLabel = false,
  hideBorder = false,
  hoverable = true,
  hideBackground = false,
  hidePrice = false,
  hideNewTag = false,
  hidePlayerRoleName = true,
  chemStyleId,
  tierMetaRating,
  playOnShow,
  evolution,
  chemistry,
}: {
  player: Player;
  asLink?: {
    basePath: BasePath;
  };
  /** Fires on navigation. Only wired up alongside `asLink`. */
  onClick?: () => void;
  scale?: number;
  fullWidth?: boolean;
  hideLabel?: boolean;
  hideBorder?: boolean;
  hidePrice?: boolean;
  hideBackground?: boolean;
  hoverable?: boolean;
  hideNewTag?: boolean;
  hidePlayerRoleName?: boolean;
  chemStyleId?: number;
  tierMetaRating?: number;
  /** Plays the card's effects once on arrival (see PlayerCard). */
  playOnShow?: boolean;
  /** Chain / Cost / Games footer for an evolution player. */
  evolution?: EvolutionFooterDetails;
  /** The player's chemistry (0–3), on the card's bottom-left corner. */
  chemistry?: Chemistry;
}) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const contentLabels = useSelector(contentLabelsSelector);
  const eventTokens = useSelector(eventTokensSelector);
  const playerRoleName = useSelector(
    playerRoleLabelSelector({ playerRoleId: player.playerRoleId }),
  );
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const tier = useSelector(
    tierSelector(
      tierMetaRating ?? player.metaRating,
      player.playerRoleId,
      true,
    ),
  );
  const versionSlug = versionUrlSlug(versionAssets?.[player.versionId]);

  const isEvolution = useMemo(() => isEvolutionFunction(player), [player]);
  const basePlayer = isEvolutionPlayer(player) ? player.basePlayer : undefined;
  const basePlayerTierLookup = useSelector(
    tierSelector(
      basePlayer?.metaRating ?? null,
      basePlayer?.playerRoleId ?? null,
      true,
    ),
  );
  const basePlayerTier = basePlayer ? basePlayerTierLookup : undefined;

  /*
    playerRoleId is deliberately not passed. It would append ?player-role-id=
    to every card link, which fragments the crawlable URL for a page whose
    content does not change — the canonical already strips it, so the variants
    are only wasted crawl.

    The detail page falls back to the role from its list context, then to its
    own default, so a click still lands on a sensible role.
  */
  const href = asLink
    ? generateNavigationUrl({
        resourceId: player.resourceId,
        name: player.name,
        evolutionPath: player.evolutionData?.evolutionPath,
        pathwayPath: player.evolutionData?.pathwayPath,
        evoPathId: player.evoPathId,
        versionSlug,
        basePath: asLink.basePath,
      })
    : undefined;

  return (
    <View.PlayerCardWithMetaRating
      player={player}
      versionAssets={versionAssets}
      assetsBaseUrl={assetsBaseUrl}
      tier={tier}
      basePlayerTier={basePlayerTier}
      href={href}
      onClick={onClick}
      scale={scale}
      fullWidth={fullWidth}
      hideLabel={hideLabel}
      hideBorder={hideBorder}
      hoverable={hoverable}
      hideBackground={hideBackground}
      hidePrice={hidePrice}
      hideNewTag={hideNewTag}
      hidePlayerRoleName={hidePlayerRoleName}
      chemStyleId={chemStyleId}
      playOnShow={playOnShow}
      contentLabels={contentLabels}
      eventTokens={eventTokens}
      playerRoleName={playerRoleName}
      evolution={evolution}
      chemistry={chemistry}
    />
  );
};

export const EvolutionUpgradePlayerCard = ({
  basePlayer,
  evolutionPlayer,
  scale,
  gap,
  hideBackground,
  hidePrice,
  showBasePlayerMetaRating,
  href,
  onClick,
  className,
}: {
  basePlayer: Player;
  evolutionPlayer: Player;
  scale?: number;
  /** Tailwind gap class between the cards and the arrow. */
  gap?: string;
  /** Renders bare content — no Card border, background, padding or hover. */
  hideBackground?: boolean;
  hidePrice?: boolean;
  /** Also puts the base player's own meta rating chip under its card. */
  showBasePlayerMetaRating?: boolean;
  /** Wraps the card in a NavLink. */
  href?: string;
  onClick?: () => void;
  className?: string;
}) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const tier = useSelector(
    tierSelector(
      evolutionPlayer.metaRating,
      evolutionPlayer.playerRoleId,
      true,
    ),
  );
  const basePlayerTier = useSelector(
    tierSelector(basePlayer.metaRating, basePlayer.playerRoleId, true),
  );
  const contentLabels = useSelector(contentLabelsSelector);
  const eventTokens = useSelector(eventTokensSelector);
  return (
    <View.EvolutionUpgradePlayerCard
      basePlayer={basePlayer}
      evolutionPlayer={evolutionPlayer}
      versionAssets={versionAssets}
      assetsBaseUrl={assetsBaseUrl}
      tier={tier}
      basePlayerTier={basePlayerTier ?? undefined}
      scale={scale}
      gap={gap}
      hideBackground={hideBackground}
      hidePrice={hidePrice}
      showBasePlayerMetaRating={showBasePlayerMetaRating}
      href={href}
      onClick={onClick}
      className={className}
      contentLabels={contentLabels}
      eventTokens={eventTokens}
    />
  );
};

export default PlayerCard;
